import * as Sentry from '@sentry/react'
import { FirebaseStorage, FullMetadata, getDownloadURL, uploadBytes } from 'firebase/storage'
import { kGcsUserFileUploadsBucketRef, PDF_MAX_PAGES } from '@/constants/constants-gcs'
import { getMetadata, ref, getBlob } from 'firebase/storage'
import { getAppStorage } from '@/firebase/storage-routing'
import { saveAs } from 'file-saver'
import { TokenLimitResponse } from '@/chat-common/selected_files/schema'
import { determineGcsFileStatus } from './gcs-file-metadata-logic'
import { GCSFileMetadataStatusIndicator } from './gcs-file-self-assessment'

/**
 * Maximum allowed token count based on the backend's token limit.
 *
 * - Backend token limit: < 80,000 tokens.
 * - Soon we will fetch this from the backend to ensure it's up to date: ENG-4619.
 *
 * - This is the default value used when the token limit is not available.
 * - Intentionally different from the backend value
 */
const DEFAULT_MAX_TOKEN_COUNT = 79999

/**
 * Number of tokens in each chunk used to create a single embedding.
 *
 * - Each embedding is derived from a chunk of 2,000 tokens.
 * - This is the default value used when the token limit is not available.
 * - Intentionally different from the backend value
 */
const DEFAULT_CHUNK_SIZE = 1999

/**
 * Construct Default TokenLimitResponse
 */
export const defaultMaxTokenResponse: TokenLimitResponse = {
  max_tokens: DEFAULT_MAX_TOKEN_COUNT,
  chunk_size: DEFAULT_CHUNK_SIZE,
}

export async function getFileMetadataFromGCS(filePath: string) {
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })
  const fileRef = ref(storage, filePath)
  const metadata = await getMetadata(fileRef)
  return metadata
}

export function sanitizeFileName(fileName: string | undefined): string {
  return (
    fileName
      ?.substring(0, 230)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_') // strip punctuation
      .replace(/\s/g, '_') // Replace spaces with underscores
      .replace(/[/\\?%*:|"<>]/g, '') // Remove invalid characters
      .replace(/^_+|_+$/g, '') // strip leading and trailing underscores
      .trim() || 'Download'
  )
}

/**
 * Download a File from GCS using kGcsUserFileUploadsBucketRef
 *
 * @param filePath - The path of the file in GCS.
 * @param fileName - The desired name for the downloaded file.
 * @returns A promise that resolves to the filename if the download was successful.
 */
export async function downloadFile(filePath: string, fileName?: string) {
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })
  const fileRef = ref(storage, filePath)
  if (!fileName) {
    const metadata = await getMetadata(fileRef)
    fileName = metadata.name
  }
  const blob = await getBlob(fileRef)
  saveAs(blob, fileName)
  return fileName
}

export async function copyFile(oldPath: string, newPath: string, storage: FirebaseStorage): Promise<void> {
  try {
    const oldFileRef = ref(storage, oldPath)
    const newFileRef = ref(storage, newPath)

    const url = await getDownloadURL(oldFileRef)

    const response = await fetch(url)
    const blob = await response.blob()

    await uploadBytes(newFileRef, blob)
  } catch (error) {
    console.error('Error copying file:', error)
    throw error
  }
}

/**
 * Retrieves the embeddings count for a file if it is done processing.
 *
 * @param {string} filePath - The file path.
 * @returns {Promise<number>} - Returns the embeddings count or 0 if invalid or not processed.
 */
export async function getFileEmbeddingsCount(filePath: string): Promise<number> {
  try {
    const metadata = await getFileMetadataFromGCS(filePath)
    const fileStatus = determineGcsFileStatus({ fileMetadata: metadata })
    const isDone = fileStatus.status === GCSFileMetadataStatusIndicator.DONE

    if (!isDone) return 0 // If the file isn't done, return 0

    const embeddingsCountStr = metadata.customMetadata?.embeddings_count
    const embeddingsCount = parseInt(embeddingsCountStr || '0', 10)

    if (isNaN(embeddingsCount)) {
      Sentry.captureException(new Error('Unable to parse integer from embeddings_count'), {
        extra: {
          fileMetadata: JSON.stringify(metadata),
        },
      })
      return 0
    }

    return embeddingsCount
  } catch (error: unknown) {
    Sentry.captureException(new Error(`Error running getFileEmbeddingsCount`), {
      extra: { filePath, error: JSON.stringify(error) },
    })
    return 0
  }
}

/**
 * Check If Files Exist In GCS
 *
 * @param {string[]} filePaths - An array of file paths in GCS to check
 * @returns {Promise<string[]>} - An array of file paths that exist in GCS
 */
export async function checkIfFilesExistInGCS(filePaths: string[]): Promise<string[]> {
  const existingFiles: string[] = []

  // Check if the file already exists at this google cloud storage reference by attempting to fetch the metadata
  for (const filePath of filePaths) {
    try {
      await getFileMetadataFromGCS(filePath)
      existingFiles.push(filePath)
    } catch {
      // do nothing
    }
  }

  return existingFiles
}

/**
 * Identify files that already exist based on the full file path
 *
 * A file is considered existing if:
 * 1. It's already in the current selection (e.g., in a create new job form - user added it from Drive or uploaded it earlier)
 * 2. It already exists in GCS at the same path (from a previous upload)
 *
 * Returns existing files (need confirmation) and new files (ready to upload immediately).
 *
 * @param {File[]} acceptedFiles - The files to check for existence
 * @param {string[]} selectedFiles - The currently selected file paths
 * @param {(fileName: string) => string} getFullFilePath - Function to build full file path for the accepted files in GCS
 * @returns {Promise<{ existingFiles: File[]; newFiles: File[] }>} - Categorized files
 */
export async function identifyExistingFiles(
  acceptedFiles: File[],
  selectedFiles: string[],
  getFullFilePath: (fileName: string) => string
): Promise<{ existingFiles: File[]; newFiles: File[] }> {
  // First pass: identify files already selected in this form
  const filesSelectedInForm: File[] = []
  const filesNotSelectedInForm: File[] = []

  acceptedFiles.forEach((file) => {
    const filePath = getFullFilePath(file.name)
    if (selectedFiles.includes(filePath)) {
      // User is trying to upload a file already in the form
      filesSelectedInForm.push(file)
    } else {
      // This file is not yet selected in the form
      filesNotSelectedInForm.push(file)
    }
  })

  // Second pass: check if the not-selected files already exist in GCS
  const notSelectedInFormFilePaths = filesNotSelectedInForm.map((file) => getFullFilePath(file.name))
  const existingInGCS = notSelectedInFormFilePaths.length > 0 ? await checkIfFilesExistInGCS(notSelectedInFormFilePaths) : []
  const alreadyExistInGCS = filesNotSelectedInForm.filter((file) => existingInGCS.includes(getFullFilePath(file.name)))

  return {
    // Files that already exist and need user confirmation
    existingFiles: [...filesSelectedInForm, ...alreadyExistInGCS],
    // Only files that are completely new (not in current form selection, not in GCS)
    newFiles: filesNotSelectedInForm.filter((file) => !alreadyExistInGCS.some((f) => f.name === file.name)),
  }
}

export function isPdfFile(file: FullMetadata | string | File | Blob): boolean {
  if (typeof file === 'string') {
    return file.toLowerCase().endsWith('.pdf')
  }

  // Validate File object
  if (file instanceof File) {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  }

  // Validate Blob object
  if (file instanceof Blob) {
    return file.type === 'application/pdf'
  }

  // Validate FullMetadata
  return file.contentType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

/**
 * Does the file have a supported viewable markdown format?
 * - Supported formats: .txt, .docx, text/plain
 *
 * @param file - Either FullMetadata object or a file path string
 * @returns {boolean} - true if the file has a supported viewable markdown format.
 */
export function hasViewableMarkdownFile(file: FullMetadata | string): boolean {
  if (typeof file === 'string') {
    const lowerPath = file.toLowerCase()
    return lowerPath.endsWith('.txt') || lowerPath.endsWith('.docx')
  }
  // Validate FullMetadata
  return file.contentType === 'text/plain' || file.name.toLowerCase().endsWith('.txt') || file.name.toLowerCase().endsWith('.docx')
}

/**
 * Validates a PDF file's page count against the maximum allowed pages.
 * Lazy loads PDF.js only when called to avoid increasing bundle size.
 *
 * @param file - The PDF File or Blob to validate
 * @returns An object with `error` and `message` fields. Returns error true if the PDF exceeds the maximum page count or cannot be read.
 */
export async function validatePdfPageCount(file: File | Blob): Promise<{ error: boolean; message: string }> {
  try {
    // Lazy load PDF.js only when validating a PDF
    const pdfjs = await import('pdfjs-dist')

    // Configure worker (only on first use)
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString()
    }

    // Convert File or Blob to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer()

    // Load the PDF document
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer })
    const pdf = await loadingTask.promise

    // Get the page count
    const actualPages = pdf.numPages

    if (actualPages > PDF_MAX_PAGES) {
      return {
        error: true,
        message: `PDF has ${actualPages} pages, which exceeds the limit of ${PDF_MAX_PAGES} pages.`,
      }
    }
  } catch (error) {
    console.error('Error reading PDF:', error)
    Sentry.captureException(error)
    return {
      error: true,
      message: 'Unable to read PDF file',
    }
  }

  return { error: false, message: '' }
}
